import type { FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { Testimonial, Tone } from '@/types'

import { useRef } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'
import { Stars } from '@/components/generals/stars'

interface VoiceReelProps {
    items: Testimonial[]
    index?: string
    title?: string
    intro?: string
    tone?: Tone
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

/** Customer voices on a horizontal reel. Drag with the mouse, swipe on touch, or use the arrows. */
export const VoiceReel: FC<VoiceReelProps> = ({
    items,
    index,
    title = 'Ekte resultater. Ekte håndverkere.',
    intro,
    tone = 'night',
    id = 'kundehistorier'
}): ReactNode => {
    const track = useRef<HTMLDivElement>(null)
    const drag = useRef({ active: false, startX: 0, scroll: 0, moved: false })

    const step = (direction: 1 | -1): void => {
        const el = track.current

        if (!el) return

        const first = el.firstElementChild as HTMLElement | null
        const width = first ? first.getBoundingClientRect().width : el.clientWidth * 0.8

        el.scrollBy({ left: direction * width, behavior: 'smooth' })
    }

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
        if (event.pointerType !== 'mouse' || !track.current) return

        drag.current = { active: true, startX: event.clientX, scroll: track.current.scrollLeft, moved: false }
    }

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
        const el = track.current
        const state = drag.current

        if (!state.active || !el) return

        const dx = event.clientX - state.startX

        if (Math.abs(dx) > 4) {
            state.moved = true
            el.dataset.dragging = 'true'
        }

        el.scrollLeft = state.scroll - dx
    }

    const endDrag = (): void => {
        drag.current.active = false

        if (track.current) track.current.dataset.dragging = 'false'
    }

    return (
        <section
            id={id}
            className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative overflow-hidden py-24 md:py-36')}
            data-header={DARK.includes(tone) ? 'dark' : 'light'}
        >
            <div className='wrap grid grid-cols-12 items-end gap-x-6 gap-y-8'>
                <div className='col-span-12 lg:col-span-7'>
                    <SectionLabel index={index}>Kundehistorier</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text={title} />
                    </h2>
                </div>

                <div className='col-span-12 flex items-end justify-between gap-6 lg:col-span-5'>
                    {intro && <p className='max-w-sm text-tone-muted pretty'>{intro}</p>}

                    <div className='ml-auto flex gap-2'>
                        <button type='button' onClick={() => step(-1)} className='flex size-12 items-center justify-center border border-tone-line transition-colors duration-500 hover:bg-tone-fg hover:text-tone' aria-label='Forrige kundehistorie'>
                            <ArrowLeft className='size-5' aria-hidden='true' />
                        </button>
                        <button type='button' onClick={() => step(1)} className='flex size-12 items-center justify-center border border-tone-line transition-colors duration-500 hover:bg-tone-fg hover:text-tone' aria-label='Neste kundehistorie'>
                            <ArrowRight className='size-5' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </div>

            <div
                ref={track}
                className='voice-track no-scrollbar mt-14 flex snap-x snap-mandatory gap-0 overflow-x-auto pl-[var(--gutter)] md:mt-20'
                data-cursor='Dra'
                data-dragging='false'
                data-lenis-prevent-horizontal
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerLeave={endDrag}
                onClickCapture={event => {
                    if (drag.current.moved) {
                        event.preventDefault()
                        event.stopPropagation()
                        drag.current.moved = false
                    }
                }}
            >
                {items.map(item => (
                    <figure
                        key={`${item.name}-${item.quote.slice(0, 12)}`}
                        className='flex w-[84vw] shrink-0 snap-start flex-col border-l border-tone-line px-6 sm:w-[26rem] md:px-8 lg:w-[30rem]'
                    >
                        {item.image && (
                            <div className='mb-8 aspect-[4/5] overflow-hidden bg-tone-line' data-reveal='clip'>
                                <img
                                    src={item.image}
                                    alt={`${item.name}, ${item.company}`}
                                    loading='lazy'
                                    decoding='async'
                                    draggable={false}
                                    className='size-full object-cover'
                                />
                            </div>
                        )}

                        <div className='flex items-center gap-4 text-tone-ink'>
                            <Stars />
                            {item.highlight && <span className='t-label text-tone-muted'>{item.highlight}</span>}
                        </div>

                        <blockquote className='mt-6 flex-1'>
                            <p className='t-h3 text-pretty'>«{item.quote}»</p>
                        </blockquote>

                        <figcaption className='mt-8 text-sm'>
                            <span className='font-semibold'>{item.name}</span>
                            <span className='text-tone-muted'> · {item.company}</span>
                        </figcaption>
                    </figure>
                ))}

                <div className='w-[var(--gutter)] shrink-0' aria-hidden='true' />
            </div>
        </section>
    )
}
